/**
 * Integrytas Group - Common Security Module (CSM)
 * Tab Manager shell with hierarchical hamburger menu and watermark.
 *
 * Aesthetic: Acumatica ERP - high-density, white background, #E5E7EB borders.
 * Multi-level menu:
 *   - Pages with children render as expandable groups (don't open a tab on click).
 *   - Leaf pages (no children) open a tab and auto-close the drawer.
 *   - Expansion state is persisted in localStorage so it survives reloads.
 *   - When the search filter is active, the menu flattens to matches across all levels.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AuthSession, PageAccess } from '../../types/auth';
import { useBranding } from '../../lib/use_branding';
import { usePresentationState, PresentationContext } from '../../lib/use_presentation';
import { useNotificationState, NotificationContext } from '../../lib/use_notifications';
import { commandRegistry } from '../../lib/command_registry';
import { Watermark } from '../common/Watermark';
import { Omnibox } from '../common/Omnibox';
import { ToastContainer } from '../common/ToastContainer';
import { NotificationBell } from '../common/NotificationBell';
import { PresentationControls } from '../common/PresentationControls';
import { ThemeSelector } from '../common/ThemeSelector';
import { DdmModeToggle } from '../common/DdmModeToggle';
import { DdmDemoBanner } from '../common/DdmDemoBanner';
import { TaskMonitor } from '../common/TaskMonitor';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { useTaskQueueState, TaskQueueContext } from '../../lib/use_task_queue';
import { use_favorites } from '../../lib/use_favorites';
import { is_nav_group_code } from '../../lib/nav_groups';
import { use_workspace_layout } from '../../lib/use_workspace_layout';
import { WorkspaceBreadcrumb } from './WorkspaceBreadcrumb';
import { TabStrip } from './TabStrip';
import { LanguageSwitcher } from './LanguageSwitcher';
import { AboutModal } from '../common/AboutModal';
import { ConfirmModal } from '../common/ConfirmModal';
import { AboutContext } from '../../lib/use_about';
import { UnsavedChangesContext, type UnsavedChangesApi } from '../../lib/use_unsaved_changes';
import { ShellChromeContext, type ShellChrome } from '../../lib/shell_chrome';
import { SuppressInlineToggleContext } from '../../lib/suppress_inline_toggle';
import { PersonalizationContext, type PersonalizationCtx } from '../../lib/use_personalization';
import { PersonalizationToggle } from '../personalization/PersonalizationToggle';
import { DataLineageToggle } from '../personalization/DataLineageToggle';
import { scopedKey } from '../../lib/storage_principal';
import { RefreshRegistryContext, type RefreshRegistry } from '../../lib/use_refresh_registry';
import { useBackGuard } from '../../lib/use_back_guard';

export interface OpenTab {
  page_internal_code: string;
  display_text: string;
  route_path: string;
}

export interface TabManagerShellProps {
  session: AuthSession;
  menu: PageAccess[];
  pageLabels: Record<string, string>;
  initialTabs?: OpenTab[];
  onSignOut: () => void;
  onChangePassword: () => void;
  onEndMimic?: () => void;
  renderPage: (tab: OpenTab) => React.ReactNode;
}

interface MenuNode {
  page: PageAccess;
  label: string;
  children: MenuNode[];
}

const EXPANSION_KEY = 'csm.menu.expanded';
const APP_VERSION = ((import.meta.env.VITE_APP_VERSION as string | undefined) ?? '').trim();

/** Builds a tree from the flat PageAccess[]. Orphans are promoted to roots. */
function buildMenuTree(menu: PageAccess[], pageLabels: Record<string, string>): MenuNode[] {
  const visible = menu.filter(p => p.can_view);
  const byId = new Map<number, MenuNode>();
  for (const p of visible) {
    byId.set(p.page_id, {
      page: p,
      label: pageLabels[p.page_internal_code] ?? p.page_internal_code,
      children: [],
    });
  }
  const roots: MenuNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.page.parent_page_id;
    const parent = parentId != null ? byId.get(parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sortFn = (a: MenuNode, b: MenuNode) =>
    (a.page.sort_order - b.page.sort_order) || a.label.localeCompare(b.label);
  const sortRecursive = (nodes: MenuNode[]) => {
    nodes.sort(sortFn);
    for (const n of nodes) sortRecursive(n.children);
  };
  sortRecursive(roots);
  return roots;
}

export function TabManagerShell({
  session, menu, pageLabels, initialTabs = [],
  onSignOut, onChangePassword, onEndMimic, renderPage,
}: TabManagerShellProps) {
  const { t } = useTranslation();
  const { appName, brandingSuffix } = useBranding();
  const presentation  = usePresentationState(session);
  const notifications = useNotificationState(session);
  const taskQueue     = useTaskQueueState(session);
  const [menuOpen,      setMenuOpen]     = useState(false);
  const [omniboxOpen,   setOmniboxOpen]  = useState(false);
  const [showAbout,     setShowAbout]    = useState(false);
  const [showUserMenu,  setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const menuSearchRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter]     = useState('');
  // Lazy-init from sessionStorage so a shell REMOUNT (auth re-hydration,
  // error-boundary recovery) restores the tabs open at that moment — the
  // initialTabs prop is memoized once at App mount and goes stale, and
  // seeding state from it let the persistence effect below overwrite the
  // stored tab list with the stale (often empty) snapshot.
  const [tabs, setTabs]         = useState<OpenTab[]>(() => {
    try {
      const raw = sessionStorage.getItem(scopedKey('csm.openTabs'));
      return raw ? (JSON.parse(raw) as OpenTab[]) : initialTabs;
    } catch { return initialTabs; }
  });
  const layout    = use_workspace_layout(
    // Same remount-safety as `tabs` above: prefer the live stored active tab — but
    // only if it is actually one of the restored open tabs. A stale active tab (e.g.
    // left over after close-all) must NOT be restored, or the breadcrumb would show a
    // page with no tab open.
    (() => {
      try {
        const openRaw = sessionStorage.getItem(scopedKey('csm.openTabs'));
        const open = openRaw ? (JSON.parse(openRaw) as OpenTab[]) : initialTabs;
        const stored = sessionStorage.getItem(scopedKey('csm.activeTab'));
        if (stored && open.some(t => t.page_internal_code === stored)) return stored;
        return open[0]?.page_internal_code ?? null;
      } catch { return initialTabs[0]?.page_internal_code ?? null; }
    })(),
  );
  const favorites = use_favorites(session, menu, pageLabels);
  const activeCode = layout.active_code;
  // Split model: the left pane holds the pinned tab; the right workspace holds
  // every OTHER open tab, with its active one visible. A tab is never in both.
  const splitPinned = layout.pane_left_code;
  const splitWorkspaceTabs = layout.is_split ? tabs.filter(t => t.page_internal_code !== splitPinned) : [];
  const splitActiveWorkspace =
    layout.pane_right_code && layout.pane_right_code !== splitPinned &&
    splitWorkspaceTabs.some(t => t.page_internal_code === layout.pane_right_code)
      ? layout.pane_right_code
      : splitWorkspaceTabs.at(-1)?.page_internal_code ?? null;
  const [expanded, setExpanded] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem(scopedKey(EXPANSION_KEY));
      return raw ? new Set<number>(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  useEffect(() => {
    sessionStorage.setItem(scopedKey('csm.openTabs'), JSON.stringify(tabs));
    if (activeCode) sessionStorage.setItem(scopedKey('csm.activeTab'), activeCode);
    else sessionStorage.removeItem(scopedKey('csm.activeTab'));   // don't leave a stale active tab after close-all
  }, [tabs, activeCode]);

  // ---- Title-bar chrome: global inspect + active-page personalization bridge ----
  const [isInspecting, setInspecting] = useState(false);
  const toggleInspecting = useCallback(() => setInspecting(v => !v), []);
  const [activePersonalization, setActivePersonalization] = useState<PersonalizationCtx | null>(null);
  const publishPersonalization = useCallback((code: string, ctx: PersonalizationCtx | null) => {
    setActivePersonalization(prev => ctx ?? (prev && prev.pageCode === code ? null : prev));
  }, []);
  const chrome = useMemo<ShellChrome>(() => ({
    isInspecting, setInspecting, toggleInspecting, activeCode: activeCode ?? '',
    activePersonalization, publishPersonalization,
  }), [isInspecting, toggleInspecting, activeCode, activePersonalization, publishPersonalization]);

  useEffect(() => {
    try { localStorage.setItem(scopedKey(EXPANSION_KEY), JSON.stringify([...expanded])); }
    catch { /* noop */ }
  }, [expanded]);

  // Keep the command registry in sync with current tabs and menu.
  useEffect(() => {
    commandRegistry.setShellContext({ menu, tabs, openTab: openTabDirect, session, pageLabels });
  }); // intentionally no deps — runs on every render to stay current

  // Global Cmd/Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOmniboxOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Opening the slide-out menu puts the caret in "Find a page…" so the user can
  // type straight away — the menu's primary action is search, not scrolling. The
  // drawer mounts with `menuOpen`, so the ref is live by the time this runs.
  //
  // The previous query is SELECTED rather than cleared: typing replaces it (which
  // is what someone reopening the menu to look for something else expects — with
  // a bare focus the new words append to the stale filter and match nothing),
  // while it stays there to refine or read.
  useEffect(() => {
    if (!menuOpen) return;
    menuSearchRef.current?.focus();
    menuSearchRef.current?.select();
  }, [menuOpen]);

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  const tree = useMemo(() => buildMenuTree(menu, pageLabels), [menu, pageLabels]);

  // Flat list used while a search filter is active (excludes non-navigable group nodes).
  const flatMatches = useMemo(() => {
    if (!filter.trim()) return [];
    const q = filter.toLowerCase();
    const out: MenuNode[] = [];
    const walk = (nodes: MenuNode[]) => {
      for (const n of nodes) {
        const isGroupNode = is_nav_group_code(n.page.page_internal_code);
        if (!isGroupNode && (
          n.label.toLowerCase().includes(q) ||
          n.page.page_internal_code.toLowerCase().includes(q)
        )) out.push(n);
        if (n.children.length > 0) walk(n.children);
      }
    };
    walk(tree);
    return out;
  }, [tree, filter]);

  // Open (or focus) a tab from a concrete OpenTab. Shared by the menu and the
  // command registry — the registry passes an OpenTab, the menu a MenuNode.
  const openTabDirect = (tab: OpenTab) => {
    setTabs(prev => prev.some(t => t.page_internal_code === tab.page_internal_code)
      ? prev
      : [...prev, tab]);
    layout.focus_code(tab.page_internal_code);   // opens in the active pane
    setMenuOpen(false);
  };

  const openLeaf = (node: MenuNode) => openTabDirect({
    page_internal_code: node.page.page_internal_code,
    display_text:       node.label,
    route_path:         node.page.route_path,
  });

  const toggleExpand = (pageId: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId); else next.add(pageId);
      return next;
    });
  };

  const closeTab = (code: string) => {
    const was_focused = layout.active_code === code;
    const was_split = layout.is_split;
    const was_pinned = layout.pane_left_code === code;    // the left-pane (pinned) tab
    const pinned = layout.pane_left_code;
    const remaining = tabs.filter(t => t.page_internal_code !== code);
    setTabs(remaining);
    layout.handle_close(code);                       // clears the code from its pane / collapses when the pin closes

    if (was_split && !was_pinned) {
      // A right-workspace tab closed while split. Keep the right pane filled with a
      // surviving non-pinned tab, or unsplit if only the pinned tab is left.
      const non_pinned = remaining.filter(t => t.page_internal_code !== pinned);
      if (non_pinned.length === 0) layout.unsplit();
      else if (was_focused || layout.pane_right_code === code) {
        layout.focus_code(non_pinned[non_pinned.length - 1].page_internal_code);
      }
      return;
    }

    if (was_focused) {
      const survivor = remaining[remaining.length - 1]?.page_internal_code;
      if (survivor) layout.focus_code(survivor);
    }
  };

  // Strip tools (Direction D). Close-all clears every tab and resets the panes;
  // the split toggle enters a side-by-side split of the active tab with the
  // next open tab, or returns to a single pane when already split.
  const closeAll = () => {
    const codes = tabs.map(t => t.page_internal_code);
    setTabs([]);
    for (const code of codes) layout.handle_close(code);
  };

  const toggleSplit = () => {
    if (layout.is_split) { layout.unsplit(); return; }
    // Needs at least two tabs: the selected one is pinned to the right pane and
    // the most recent other tab opens in the left workspace.
    if (tabs.length < 2 || !activeCode) return;
    const other = tabs.filter(t => t.page_internal_code !== activeCode).at(-1);
    if (!other) return;
    layout.split_with(activeCode, other.page_internal_code);
  };

  // ── Refresh registry (T1.2) ───────────────────────────────────────────────
  // Pages register their reload via usePageRefresh; the strip's Refresh button
  // reloads whichever page is active. The button appears only when the active
  // tab has a registered reloader.
  const reloadersRef = useRef<Map<string, () => void>>(new Map());
  const [refreshableKeys, setRefreshableKeys] = useState<Set<string>>(new Set());
  const refreshRegistry = useMemo<RefreshRegistry>(() => ({
    register: (pageCode, reload) => {
      reloadersRef.current.set(pageCode, reload);
      setRefreshableKeys(k => (k.has(pageCode) ? k : new Set(k).add(pageCode)));
      return () => {
        reloadersRef.current.delete(pageCode);
        setRefreshableKeys(k => { if (!k.has(pageCode)) return k; const n = new Set(k); n.delete(pageCode); return n; });
      };
    },
  }), []);
  const activeRefresh = (activeCode && refreshableKeys.has(activeCode))
    ? () => reloadersRef.current.get(activeCode)?.()
    : undefined;

  // ── Unsaved-changes tracking (per workspace tab) ──────────────────────────
  // Forms publish their dirty state here (keyed by owning page code) via
  // useUnsavedChanges / useDirtyForm. A dirty tab shows an amber dot in place of
  // its close control (TabStrip), and closing it — or close-all — is confirmed.
  const [dirtySources, setDirtySources] = useState<Record<string, { code: string; dirty: boolean }>>({});
  const setDirtySource = useCallback<UnsavedChangesApi['setSource']>((id, code, dirty) => {
    setDirtySources(prev => {
      if (code && dirty) {
        const cur = prev[id];
        if (cur && cur.code === code) return prev;
        return { ...prev, [id]: { code, dirty: true } };
      }
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...next } = prev;
      return next;
    });
  }, []);
  const unsavedApi = useMemo<UnsavedChangesApi>(() => ({ setSource: setDirtySource }), [setDirtySource]);
  const dirtyCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const v of Object.values(dirtySources)) if (v.dirty) codes.add(v.code);
    return codes;
  }, [dirtySources]);
  const isTabDirty = useCallback((code: string) => dirtyCodes.has(code), [dirtyCodes]);

  // Warn on a browser refresh / close while any tab holds unsaved changes.
  useEffect(() => {
    if (dirtyCodes.size === 0) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyCodes.size]);

  // Close is guarded when the target holds unsaved changes.
  const [pendingClose, setPendingClose] = useState<{ kind: 'one'; code: string } | { kind: 'all' } | null>(null);
  const requestCloseTab = (code: string) => {
    if (isTabDirty(code)) setPendingClose({ kind: 'one', code });
    else closeTab(code);
  };
  const requestCloseAll = () => {
    if (tabs.some(t => isTabDirty(t.page_internal_code))) setPendingClose({ kind: 'all' });
    else closeAll();
  };
  const confirmPendingClose = () => {
    if (pendingClose?.kind === 'one') closeTab(pendingClose.code);
    else if (pendingClose?.kind === 'all') closeAll();
    setPendingClose(null);
  };

  const mimicActive = session.mimic.is_active;

  // ── Back-button guard (T1.3) ──────────────────────────────────────────────
  // Tabs live in sessionStorage, not history, so Back would leave the app. With
  // tabs open, Back returns to the menu (closes tabs); at the menu, it confirms.
  useBackGuard({
    hasTabs: tabs.length > 0,
    goToMenu: requestCloseAll,
    confirmLeaveMessage: t('shell.confirm_leave', 'Do you really want to leave {{app}}?', { app: appName }),
  });

  return (
    <ShellChromeContext.Provider value={chrome}>
    <AboutContext.Provider value={{ openAbout: () => setShowAbout(true) }}>
    <PresentationContext.Provider value={presentation}>
    <NotificationContext.Provider value={notifications}>
    <TaskQueueContext.Provider value={taskQueue}>
    <UnsavedChangesContext.Provider value={unsavedApi}>
    <RefreshRegistryContext.Provider value={refreshRegistry}>
    <div className="relative flex h-screen w-screen flex-col text-csm-md text-csm-ink"
         style={{ background: 'var(--color-secondary-bg)' }}>
      <Watermark appName={appName} brandingSuffix={brandingSuffix} />
      <DdmDemoBanner />

      {/* Top app bar - Integrytas Blue, white text. Inline style is a
          defensive fallback in case the Tailwind `bg-primary` class hasn't
          been picked up by the JIT yet (a dev-server restart usually fixes
          that, but the inline rule guarantees correct color either way). */}
      <header
        className="relative z-[55] flex h-10 shrink-0 items-center bg-primary px-2 text-white"
        style={{ background: 'var(--color-primary)' }}
      >
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label={t('shell.open_menu')}
          className="mr-2 flex h-8 w-8 flex-col items-center justify-center gap-[3px] rounded border border-white/25 hover:bg-white/10"
        >
          {/* Three explicit bars - no Tailwind shadow trickery, just blocks. */}
          <span className="block h-0.5 w-4 bg-white" />
          <span className="block h-0.5 w-4 bg-white" />
          <span className="block h-0.5 w-4 bg-white" />
        </button>

        <div className="text-csm-sm font-semibold uppercase tracking-wider">
          {appName}
        </div>

        {mimicActive && (
          <div className="ml-4 flex items-center gap-2 rounded border border-csm-warning-border bg-csm-warning-soft px-2 py-0.5 text-csm-xs text-csm-warning">
            <span>
              {t('shell.mimic_active', {
                name: session.effective_user.display_name || session.effective_user.email,
              })}
            </span>
            {onEndMimic && (
              <button type="button" onClick={onEndMimic}
                className="rounded border border-csm-warning-border bg-white px-1.5 py-0.5 font-medium text-csm-warning hover:bg-csm-warning-soft">
                {t('shell.mimic_exit', 'Exit mimic')}
              </button>
            )}
          </div>
        )}

        {/* Right-side header controls */}
        <div className="ml-auto flex items-center gap-1 text-csm-xs text-white/90">
          {/* Cmd+K hint */}
          <button
            type="button"
            onClick={() => setOmniboxOpen(true)}
            aria-label={t('omnibox.open_label')}
            title="Cmd+K"
            className="hidden sm:flex h-7 items-center gap-1.5 rounded border
                       border-white/20 bg-white/5 px-2 text-csm-xs text-white/70
                       hover:bg-white/10 hover:text-white mr-1"
          >
            <span>⌕</span>
            <span>{t('omnibox.search_hint')}</span>
            <kbd className="rounded border border-white/20 px-1 font-mono text-csm-2xs">⌘K</kbd>
          </button>
          <PresentationControls />
          <NotificationBell
            notifications={notifications.notifications}
            unreadCount={notifications.unreadCount}
            onMarkRead={notifications.markRead}
            onMarkAllRead={notifications.markAllRead}
          />
          {/* Active-page personalization (bridged from the focused tab) + global
              data-source Inspect — one place, sized to match the About icon. */}
          {chrome.activePersonalization && (
            <PersonalizationContext.Provider value={chrome.activePersonalization}>
              <PersonalizationToggle variant="header" />
            </PersonalizationContext.Provider>
          )}
          <DataLineageToggle />
          <button
            type="button"
            onClick={() => setShowAbout(true)}
            aria-label={t('about.open_label', 'About')}
            title={t('about.open_label', 'About')}
            className="flex h-7 w-7 items-center justify-center rounded text-white/80 hover:bg-white/10 hover:text-white"
          >
            <Info className="h-4 w-4" />
          </button>
          <ThemeSelector />
          <LanguageSwitcher session={session} />
          <DdmModeToggle />
          <div className="mx-1 h-5 w-px bg-white/20" />
          {/* Identity chip — name/email + avatar dropdown */}
          <div ref={userMenuRef} className="relative flex items-center gap-2 text-csm-xs text-white/90">
            <div className="text-right leading-tight">
              <div className="font-medium">{session.effective_user.display_name}</div>
              <div className="text-white/70">{session.effective_user.email}</div>
            </div>
            <button
              type="button"
              onClick={() => setShowUserMenu(v => !v)}
              aria-label={t('shell.user_menu_label', 'User menu')}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-white/30 bg-white/10 text-csm-xs font-semibold uppercase hover:bg-white/25"
            >
              {session.effective_user.display_name.trim().charAt(0) || '?'}
            </button>
            {showUserMenu && (
              <div
                role="menu"
                className="absolute right-0 top-full z-[200] mt-1 w-44 rounded border border-csm-border bg-white py-1 elev-1 anim-pop"
              >
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => { setShowUserMenu(false); onChangePassword(); }}
                  className="w-full px-3 py-2 text-left text-csm-sm text-csm-ink hover:bg-csm-surface-alt"
                >
                  {t('auth.change_password')}
                </button>
                <div className="my-1 border-t border-csm-border" />
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => { setShowUserMenu(false); onSignOut(); }}
                  className="w-full px-3 py-2 text-left text-csm-sm text-red-600 hover:bg-csm-surface-alt"
                >
                  {t('auth.sign_out')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Tab strip — Multi-Tab Manager (Direction D). Discrete tabs with module
          icons, an amber unsaved-dot slot, an overflow "N more" chip, and the
          split-screen / close-all tools. See TabStrip. */}
      <TabStrip
        tabs={tabs}
        activeCode={activeCode}
        pageLabels={pageLabels}
        isSplit={layout.is_split}
        paneLeftCode={layout.pane_left_code}
        paneRightCode={layout.pane_right_code}
        onFocus={layout.focus_code}
        onCloseTab={requestCloseTab}
        onCloseAll={requestCloseAll}
        onToggleSplit={toggleSplit}
        onRefresh={activeRefresh}
        isDirty={isTabDirty}
      />

      {/*
        GLOBAL PAGE CONTAINER (Direction D layout — CLAUDE.md §11)
        ----------------------------------------------------------
        Page content is FULL-BLEED: the workspace surface spans the whole
        width with the page's own 24px canvas padding. The previous centred
        max-width:1440px container was withdrawn because ERP grids are wide
        and the gutters wasted horizontal space relational data needs.

        Inactive tabs are rendered with `hidden` so React preserves their
        local state on tab switch ("switchable without state loss"). Only
        the active tab is interactive; the rest stay mounted but invisible.

        Pages keep using `flex h-full flex-col` as their root and inherit
        the full available width / height.
      */}
      {/* Breadcrumb path bar for the focused tab (Feature 2) — only when that tab is
          actually open, so a stale active code never shows a path with no open tabs. */}
      {activeCode && tabs.some(t => t.page_internal_code === activeCode) && (
        <WorkspaceBreadcrumb
          active_code={activeCode}
          menu={menu}
          page_labels={pageLabels}
          on_navigate={openTabDirect}
        />
      )}

      <SuppressInlineToggleContext.Provider value={true}>
      <main className="flex-1 overflow-hidden">
        <div className="flex h-full w-full bg-white">
          {tabs.length === 0 ? (
            <div className="flex h-full w-full items-center justify-center text-csm-watermark">
              {t('shell.select_a_page')}
            </div>
          ) : layout.is_split ? (
            /* Split view (Feature 4) — LEFT: the single pinned tab. RIGHT: a
               workspace for every non-pinned tab (all mounted, only the active
               one visible, so switching preserves state). The pinned tab is never
               rendered on the right, so it is never displayed twice. */
            <>
              <WorkspacePane code={splitPinned} is_active={layout.active_pane === 'left'} on_focus={() => layout.focus_pane('left')} tabs={tabs} session={session} renderPage={renderPage} />
              <div className="w-px shrink-0 bg-csm-border-strong" />
              <div
                onMouseDown={() => layout.focus_pane('right')}
                className={'flex h-full min-w-0 flex-1 flex-col overflow-auto ' + (layout.active_pane === 'right' ? 'ring-1 ring-inset ring-primary/40' : '')}
              >
                {splitWorkspaceTabs.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-csm-sm text-csm-watermark">{t('shell.fill_pane', 'Click a tab to fill this pane')}</div>
                ) : (
                  splitWorkspaceTabs.map(tab => {
                    const isActive = tab.page_internal_code === splitActiveWorkspace;
                    return (
                      <div
                        key={tab.page_internal_code}
                        className={'flex-1 min-h-0 overflow-auto ' + (isActive ? '' : 'hidden')}
                        aria-hidden={!isActive}
                      >
                        <ErrorBoundary pageCode={tab.page_internal_code} session={session}>
                          {renderPage(tab)}
                        </ErrorBoundary>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            /* Single view — all tabs mounted, only the active visible (state preserved). */
            <div className="flex h-full w-full flex-col">
              {tabs.map(tab => {
                const isActive = tab.page_internal_code === activeCode;
                return (
                  <div
                    key={tab.page_internal_code}
                    className={'flex-1 min-h-0 overflow-auto ' + (isActive ? '' : 'hidden')}
                    aria-hidden={!isActive}
                  >
                    <ErrorBoundary pageCode={tab.page_internal_code} session={session}>
                      {renderPage(tab)}
                    </ErrorBoundary>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      </SuppressInlineToggleContext.Provider>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-[56] bg-black/40" onClick={() => setMenuOpen(false)} />
          <aside className="fixed left-0 top-0 z-[57] flex h-full w-72 flex-col bg-sidebar text-sidebar-text elev-2 anim-drawer">
            {/* Centered logo header per brand standard */}
            <div className="flex flex-col items-center justify-center border-b border-white/10 bg-sidebar-alt px-4 py-4">
              <div className="text-csm-md font-semibold uppercase tracking-wider text-white">
                {appName}
              </div>
              <div className="mt-0.5 text-csm-2xs uppercase tracking-wider text-sidebar-muted">
                {brandingSuffix}
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label={t('shell.close_menu')}
                className="absolute right-2 top-2 text-sidebar-muted hover:text-white"
              >x</button>
            </div>

            <div className="border-b border-white/10 p-2">
              <input
                ref={menuSearchRef}
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('shell.find_a_page')}
                className="h-8 w-full rounded border border-white/15 px-2 text-csm-md focus:border-primary focus:outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.07)', color: '#ffffff', caretColor: '#ffffff' }}
              />
            </div>

            <nav className="flex-1 overflow-y-auto py-1">
              {/* Quick Access — pinned favorites (Feature 3) */}
              {!filter.trim() && favorites.quick_access_pins.length > 0 && (
                <div className="mb-1 border-b border-white/10 pb-1">
                  <div className="px-3 py-1 text-csm-2xs font-semibold uppercase tracking-wider text-sidebar-muted">{t('shell.quick_access')}</div>
                  {favorites.quick_access_pins.map(pin => (
                    <div key={pin.page_internal_code} className="group flex items-center hover:bg-white/5">
                      <button type="button" onClick={() => openTabDirect(pin)} className="flex flex-1 items-center gap-2 py-1.5 pl-6 pr-1 text-left text-csm-md text-white">
                        <span aria-hidden className="text-csm-warning-border">★</span>
                        <span className="flex-1 truncate">{pin.display_text}</span>
                      </button>
                      <button type="button" onClick={() => favorites.toggle_pin(pin.page_internal_code)} aria-label={t('shell.unpin_label', `Unpin ${pin.display_text}`)} className="mr-2 text-sidebar-muted hover:text-white">×</button>
                    </div>
                  ))}
                </div>
              )}
              {filter.trim() ? (
                flatMatches.length === 0 ? (
                  <div className="px-3 py-2 text-csm-sm italic text-sidebar-muted">{t('shell.no_matching_pages')}</div>
                ) : (
                  flatMatches.map(node => (
                    <MenuLeaf key={node.page.page_id} node={node} depth={0} onOpen={openLeaf} is_pinned={favorites.is_pinned} on_toggle_pin={favorites.toggle_pin} />
                  ))
                )
              ) : (
                tree.length === 0 ? (
                  <div className="px-3 py-2 text-csm-sm italic text-sidebar-muted">{t('shell.no_pages_available')}</div>
                ) : (
                  tree.map(node => (
                    <MenuNodeView
                      key={node.page.page_id}
                      node={node}
                      depth={0}
                      expanded={expanded}
                      onToggle={toggleExpand}
                      onOpen={openLeaf}
                      is_pinned={favorites.is_pinned}
                      on_toggle_pin={favorites.toggle_pin}
                    />
                  ))
                )
              )}
            </nav>

            <footer className="border-t border-white/10 bg-sidebar-alt px-3 py-2">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setShowAbout(true); }}
                className="flex w-full items-center justify-between rounded border border-white/15 bg-white/5 px-2 py-1 text-csm-xs text-white/60 hover:bg-white/10 hover:text-white/90"
              >
                <span>{t('about.open_label', 'About')} {appName}</span>
                {APP_VERSION && <span className="text-white/35">v{APP_VERSION}</span>}
              </button>
            </footer>
          </aside>
        </>
      )}

      {/* ── Global overlays ──────────────────────────────────────────── */}
      <TaskMonitor />

      <AboutModal
        open={showAbout}
        onClose={() => setShowAbout(false)}
        activePageCode={activeCode}
        appName={appName}
        isAuthenticated
      />
      {omniboxOpen && <Omnibox onClose={() => setOmniboxOpen(false)} />}
      <ToastContainer
        toasts={notifications.toasts}
        onDismiss={notifications.dismissToast}
      />

      {/* Unsaved-changes close guard (Step 3). */}
      {pendingClose && (
        <ConfirmModal
          title={t('shell.unsaved_close_title', 'Discard unsaved changes?')}
          message={pendingClose.kind === 'all'
            ? t('shell.unsaved_close_all', 'One or more open tabs have unsaved changes. Close all tabs and discard them?')
            : t('shell.unsaved_close_one', 'This tab has unsaved changes. Close it and discard them?')}
          confirmLabel={t('shell.unsaved_close_confirm', 'Discard & close')}
          cancelLabel={t('common.cancel', 'Cancel')}
          onConfirm={confirmPendingClose}
          onCancel={() => setPendingClose(null)}
        />
      )}
    </div>
    </RefreshRegistryContext.Provider>
    </UnsavedChangesContext.Provider>
    </TaskQueueContext.Provider>
    </NotificationContext.Provider>
    </PresentationContext.Provider>
    </AboutContext.Provider>
    </ShellChromeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Recursive menu node
// ---------------------------------------------------------------------------

interface MenuNodeViewProps {
  node: MenuNode;
  depth: number;
  expanded: Set<number>;
  onToggle: (pageId: number) => void;
  onOpen: (node: MenuNode) => void;
  is_pinned: (page_internal_code: string) => boolean;
  on_toggle_pin: (page_internal_code: string) => void;
}

function MenuNodeView({ node, depth, expanded, onToggle, onOpen, is_pinned, on_toggle_pin }: MenuNodeViewProps) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.page.page_id);

  if (hasChildren) {
    return (
      <>
        <button
          type="button"
          onClick={() => onToggle(node.page.page_id)}
          className="flex w-full items-center gap-1 border-b border-white/5 py-2 pr-3 text-left text-csm-md font-medium text-white hover:bg-white/5"
          style={{ paddingLeft: 12 + depth * 14 }}
        >
          <ChevronRight aria-hidden className={`h-3 w-3 shrink-0 text-sidebar-muted transition-transform ${isOpen ? 'rotate-90' : ''}`} />
          <span className="flex-1">{node.label}</span>
        </button>
        {isOpen && node.children.map(child => (
          <MenuNodeView
            key={child.page.page_id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
            onOpen={onOpen}
            is_pinned={is_pinned}
            on_toggle_pin={on_toggle_pin}
          />
        ))}
      </>
    );
  }

  return <MenuLeaf node={node} depth={depth} onOpen={onOpen} is_pinned={is_pinned} on_toggle_pin={on_toggle_pin} />;
}

interface MenuLeafProps {
  node: MenuNode;
  depth: number;
  onOpen: (node: MenuNode) => void;
  is_pinned: (page_internal_code: string) => boolean;
  on_toggle_pin: (page_internal_code: string) => void;
}

function MenuLeaf({ node, depth, onOpen, is_pinned, on_toggle_pin }: MenuLeafProps) {
  const { t } = useTranslation();
  const pinned = is_pinned(node.page.page_internal_code);
  return (
    <div className="group flex items-center border-b border-white/5 hover:bg-white/5">
      <button
        type="button"
        onClick={() => onOpen(node)}
        className="block flex-1 py-2 pr-2 text-left text-csm-md text-white"
        style={{ paddingLeft: 24 + depth * 14 }}
      >
        {node.label}
      </button>
      {/* Favorites / pin toggle (Feature 3) */}
      <button
        type="button"
        onClick={() => on_toggle_pin(node.page.page_internal_code)}
        aria-label={pinned ? t('shell.unpin_label', `Unpin ${node.label}`) : t('shell.pin_label', `Pin ${node.label}`)}
        title={pinned ? t('shell.pinned_title', 'Pinned to Quick Access') : t('shell.pin_title', 'Pin to Quick Access')}
        className={'mr-2 h-5 w-5 shrink-0 rounded-sm text-csm-sm ' + (pinned ? 'text-csm-warning-border' : 'text-transparent group-hover:text-sidebar-muted hover:!text-white')}
      >{pinned ? '★' : '☆'}</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Split-view pane (Feature 4) — mounts the one tab focused into this pane.
// ---------------------------------------------------------------------------

interface WorkspacePaneProps {
  code: string | null;
  is_active: boolean;
  on_focus: () => void;
  tabs: OpenTab[];
  session: AuthSession;
  renderPage: (tab: OpenTab) => React.ReactNode;
}

function WorkspacePane({ code, is_active, on_focus, tabs, session, renderPage }: WorkspacePaneProps) {
  const { t: translate } = useTranslation();
  const tab = tabs.find(t => t.page_internal_code === code) ?? null;
  return (
    <div
      onMouseDown={on_focus}
      className={'flex h-full min-w-0 flex-1 flex-col overflow-auto ' + (is_active ? 'ring-1 ring-inset ring-primary/40' : '')}
    >
      {tab ? (
        <ErrorBoundary pageCode={tab.page_internal_code} session={session}>
          {renderPage(tab)}
        </ErrorBoundary>
      ) : (
        <div className="flex h-full items-center justify-center text-csm-sm text-csm-watermark">{translate('shell.fill_pane', 'Click a tab to fill this pane')}</div>
      )}
    </div>
  );
}
